import { Field, FieldArray } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';

interface SurveyQuestion {
    question: string;
    answerTypeId: number;
    id?: number;
    isDeleted?: boolean;
}

interface DataCollectionSettingsProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
}

export default function DataCollectionSettings({
    values,
    setFieldValue,
}: DataCollectionSettingsProps) {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-base font-semibold text-foreground">
                    Data Collection Settings
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                    Configure surveys, disclaimers, and data collection
                    preferences.
                </p>
            </div>

            {/* Meta Pixel */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Analytics
                    </h4>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="metaPixelId">Meta Pixel ID</Label>
                    <Field
                        as={Input}
                        id="metaPixelId"
                        name="metaPixelId"
                        placeholder="Enter Meta Pixel ID"
                    />
                </div>
            </div>

            {/* Disclaimer Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Disclaimer Settings
                    </h4>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="disclaimerEnabled"
                            name="disclaimerEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="disclaimerEnabled"
                            className="text-sm font-medium"
                        >
                            Enable disclaimer for sharing
                        </Label>
                    </div>
                </div>

                {values.disclaimerEnabled && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="disclaimerRequired"
                                    name="disclaimerRequired"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                                />
                                <Label
                                    htmlFor="disclaimerRequired"
                                    className="text-sm font-medium"
                                >
                                    Require disclaimer acceptance for sharing
                                </Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="disclaimerOptOut"
                                    name="disclaimerOptOut"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                                />
                                <Label
                                    htmlFor="disclaimerOptOut"
                                    className="text-sm font-medium"
                                >
                                    Is opt-out disclaimer
                                </Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="disclaimer">Disclaimer Text</Label>
                            <Field
                                as={Textarea}
                                id="disclaimer"
                                name="disclaimer"
                                rows={4}
                                placeholder="Enter disclaimer text"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Separator */}
            <div className="border-t border-border my-8"></div>

            {/* Survey Settings */}
            {/* Style & Branding */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        External Survey Settings
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Configure survey settings for externally hosted surveys.
                        These surveys must conform to the APIs defined in the
                        <a
                            href="https://www.notion.so/1ba256f15b7380448409cf5aba79ff57?v=1ba256f15b7380329743000cde743efc&source=copy_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-600"
                        >
                            &nbsp;documentation&nbsp;
                        </a>
                        to properly function.
                    </p>
                </div>

                <FeatureFlag flag={FeatureFlags.SURVEY_URL_V1}>
                    <div className="space-y-2">
                        <Label htmlFor="surveyUrl">Post-Share Survey URL</Label>
                        <Field
                            as={Input}
                            id="surveyUrl"
                            name="surveyUrl"
                            type="url"
                            placeholder="https://example.com/survey"
                        />
                    </div>
                </FeatureFlag>

                <div className="space-y-2">
                    <Label htmlFor="preShareSurveyUrl">
                        Pre-Share Survey URL
                    </Label>
                    <Field
                        as={Input}
                        id="preShareSurveyUrl"
                        name="preShareSurveyUrl"
                        type="url"
                        placeholder="https://example.com/pre-survey"
                    />
                </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border my-8"></div>

            {/* Survey Settings */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Internal Survey Settings
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Configure survey settings for in-house surveys.
                    </p>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="surveyRequired"
                            name="surveyRequired"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="surveyRequired"
                            className="text-sm font-medium"
                        >
                            Require survey completion for sharing
                        </Label>
                    </div>
                </div>

                {values.surveyRequired && (
                    <div className="space-y-4">
                        <div>
                            <h5 className="text-sm font-medium text-foreground">
                                Survey Questions
                            </h5>
                        </div>

                        <FieldArray
                            name="surveyQuestions"
                            render={(arrayHelpers) => (
                                <div className="space-y-4">
                                    {values.surveyQuestions &&
                                    values.surveyQuestions.length > 0
                                        ? values.surveyQuestions.map(
                                              (
                                                  question: SurveyQuestion,
                                                  index: number,
                                              ) => {
                                                  return question.isDeleted ? null : (
                                                      <div
                                                          key={`${values.surveyQuestions.length}-${index}`}
                                                          className="border border-border rounded-lg p-4 space-y-3"
                                                      >
                                                          <div className="flex items-center justify-between">
                                                              <span className="text-sm font-medium text-foreground">
                                                                  Question{' '}
                                                                  {index + 1}
                                                              </span>
                                                              <div className="flex items-center space-x-2">
                                                                  <Button
                                                                      type="button"
                                                                      variant="outline"
                                                                      size="sm"
                                                                      onClick={() =>
                                                                          arrayHelpers.push(
                                                                              {
                                                                                  question:
                                                                                      '',
                                                                                  answerTypeId: 1,
                                                                              },
                                                                          )
                                                                      }
                                                                  >
                                                                      +
                                                                  </Button>
                                                                  <Button
                                                                      type="button"
                                                                      variant="outline"
                                                                      size="sm"
                                                                      onClick={() =>
                                                                          arrayHelpers.replace(
                                                                              index,
                                                                              {
                                                                                  ...values
                                                                                      .surveyQuestions[
                                                                                      index
                                                                                  ],
                                                                                  isDeleted:
                                                                                      true,
                                                                              },
                                                                          )
                                                                      }
                                                                  >
                                                                      -
                                                                  </Button>
                                                              </div>
                                                          </div>

                                                          <div className="space-y-2">
                                                              <Label
                                                                  htmlFor={`surveyQuestions.${index}.question`}
                                                              >
                                                                  Question Text
                                                              </Label>
                                                              <Field
                                                                  as={Input}
                                                                  id={`surveyQuestions.${index}.question`}
                                                                  name={`surveyQuestions.${index}.question`}
                                                                  placeholder="Enter your question"
                                                              />
                                                          </div>

                                                          <div className="space-y-2">
                                                              <Label
                                                                  htmlFor={`surveyQuestions.${index}.answerTypeId`}
                                                              >
                                                                  Answer Type
                                                              </Label>
                                                              <Field
                                                                  as="select"
                                                                  id={`surveyQuestions.${index}.answerTypeId`}
                                                                  name={`surveyQuestions.${index}.answerTypeId`}
                                                                  className="block w-full h-9 border border-input rounded-md bg-transparent px-3 py-1 text-sm"
                                                              >
                                                                  <option
                                                                      value={2}
                                                                  >
                                                                      Text
                                                                  </option>
                                                                  <option
                                                                      value={1}
                                                                  >
                                                                      Yes/No
                                                                  </option>
                                                              </Field>
                                                          </div>
                                                      </div>
                                                  );
                                              },
                                          )
                                        : null}

                                    {(!values.surveyQuestions ||
                                        values.surveyQuestions.length < 1) && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() =>
                                                arrayHelpers.push({
                                                    question: '',
                                                    answerTypeId: 1,
                                                })
                                            }
                                        >
                                            Add Question
                                        </Button>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                )}
            </div>

            {/* Age Verification */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Age Verification
                    </h4>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="ageVerification"
                            name="ageVerification"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="ageVerification"
                            className="text-sm font-medium"
                        >
                            Require age verification for sharing
                        </Label>
                    </div>
                </div>

                {values.ageVerification && (
                    <div className="space-y-2">
                        <Label htmlFor="verifiedAge">Minimum Age</Label>
                        <Field
                            as={Input}
                            id="verifiedAge"
                            name="verifiedAge"
                            type="number"
                            placeholder="18"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
